import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export const PLAN_CONFIG = {
  free: {
    id: 'free',
    name: 'Glow Starter',
    price: 0,
    accent: '#94a3b8',
    badgeColor: 'bg-stone-100 text-stone-500',
    features: {
      maxEntriesPerMonth: 10,
      maxPhotos: 10,
      photos: true,
      timelapse: false,
      analytics: false,
      wishlist: false,
      exportCsv: false,
      exportJson: false,
      advancedInsights: false,
      prioritySupport: false,
      unlimitedRoutineProducts: false,
    },
  },
  glow: {
    id: 'glow',
    name: 'Glow',
    price: 14.99,
    accent: '#5C8A78',
    badgeColor: 'bg-teal-50 text-teal-700',
    stripePriceId: 'price_glow_annual_placeholder',
    features: {
      maxEntriesPerMonth: Infinity,
      maxPhotos: 100,
      photos: true,
      timelapse: true,
      analytics: true,
      wishlist: true,
      exportCsv: true,
      exportJson: false,
      advancedInsights: false,
      prioritySupport: false,
      unlimitedRoutineProducts: true,
    },
  },
  radiant: {
    id: 'radiant',
    name: 'Radiant Pro',
    price: 24.99,
    accent: '#7c3aed',
    badgeColor: 'bg-violet-50 text-violet-700',
    stripePriceId: 'price_radiant_annual_placeholder',
    features: {
      maxEntriesPerMonth: Infinity,
      maxPhotos: Infinity,
      photos: true,
      timelapse: true,
      analytics: true,
      wishlist: true,
      exportCsv: true,
      exportJson: true,
      advancedInsights: true,
      prioritySupport: true,
      unlimitedRoutineProducts: true,
    },
  },
};

const SubscriptionContext = createContext(null);

function loadTrial() {
  try {
    const raw = localStorage.getItem('glowlytix_trial');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function trialDaysRemaining(trial) {
  if (!trial) return null;
  const diff = new Date(trial.endsAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function SubscriptionProvider({ children }) {
  const [planId, setPlanId] = useState(
    () => localStorage.getItem('glowlytix_plan') || 'free'
  );

  const [billing, setBilling] = useState(() => {
    try {
      const saved = localStorage.getItem('glowlytix_billing');
      return saved ? JSON.parse(saved) : { paymentMethod: null, nextBillingDate: null, history: [] };
    } catch {
      return { paymentMethod: null, nextBillingDate: null, history: [] };
    }
  });

  const [trial, setTrial] = useState(loadTrial);

  // Check trial expiry on mount and whenever trial changes
  useEffect(() => {
    if (!trial) return;
    const daysLeft = trialDaysRemaining(trial);
    if (daysLeft <= 0 && !billing.paymentMethod) {
      setPlanId('free');
      localStorage.setItem('glowlytix_plan', 'free');
      setTrial(null);
      localStorage.removeItem('glowlytix_trial');
    }
  }, [trial, billing.paymentMethod]);

  const plan = PLAN_CONFIG[planId] || PLAN_CONFIG.free;
  const daysLeft = trialDaysRemaining(trial);
  const isOnTrial = !!trial && daysLeft > 0;

  const can = useCallback(
    (feature) => !!plan.features[feature],
    [plan]
  );

  const canLog = useCallback(
    (currentMonthCount) => currentMonthCount < plan.features.maxEntriesPerMonth,
    [plan]
  );

  const canPhoto = useCallback(
    (totalPhotoCount) => totalPhotoCount < plan.features.maxPhotos,
    [plan]
  );

  const startTrial = useCallback((trialPlanId = 'radiant', days = 14) => {
    // Don't reset an active trial
    const existing = loadTrial();
    if (existing && trialDaysRemaining(existing) > 0) return;

    const endsAt = new Date();
    endsAt.setDate(endsAt.getDate() + days);
    const trialData = { planId: trialPlanId, endsAt: endsAt.toISOString() };

    setTrial(trialData);
    localStorage.setItem('glowlytix_trial', JSON.stringify(trialData));
    setPlanId(trialPlanId);
    localStorage.setItem('glowlytix_plan', trialPlanId);
  }, []);

  const upgradeTo = useCallback((newPlanId, paymentMethod) => {
    const newPlan = PLAN_CONFIG[newPlanId];
    if (!newPlan) return;

    // Paying user — clear any trial
    setTrial(null);
    localStorage.removeItem('glowlytix_trial');

    setPlanId(newPlanId);
    localStorage.setItem('glowlytix_plan', newPlanId);

    const nextDate = new Date();
    nextDate.setFullYear(nextDate.getFullYear() + 1);

    const newBilling = {
      paymentMethod: paymentMethod || billing.paymentMethod,
      nextBillingDate: nextDate.toISOString().split('T')[0],
      history: [
        {
          id: `inv_${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          description: `${newPlan.name} — Annual`,
          amount: newPlan.price,
          status: 'Paid',
        },
        ...billing.history,
      ],
    };

    setBilling(newBilling);
    localStorage.setItem('glowlytix_billing', JSON.stringify(newBilling));
  }, [billing]);

  const cancelSubscription = useCallback(() => {
    setPlanId('free');
    localStorage.setItem('glowlytix_plan', 'free');
    setTrial(null);
    localStorage.removeItem('glowlytix_trial');

    const newBilling = { ...billing, paymentMethod: null, nextBillingDate: null };
    setBilling(newBilling);
    localStorage.setItem('glowlytix_billing', JSON.stringify(newBilling));
  }, [billing]);

  // Sync plan from server (called after login or returning from Stripe Checkout).
  // Server is the source of truth for paid plan status.
  const syncPlanFromServer = useCallback((serverPlanId) => {
    if (!serverPlanId) return;
    const validPlan = PLAN_CONFIG[serverPlanId] ? serverPlanId : 'free';
    setPlanId(validPlan);
    localStorage.setItem('glowlytix_plan', validPlan);
    // If the server shows a paid plan, clear any stale trial state
    if (validPlan !== 'free') {
      setTrial(null);
      localStorage.removeItem('glowlytix_trial');
    }
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        planId, plan, planConfig: PLAN_CONFIG,
        can, canLog, canPhoto,
        upgradeTo, cancelSubscription, syncPlanFromServer,
        billing,
        trial, isOnTrial, trialDaysLeft: daysLeft,
        startTrial,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be inside SubscriptionProvider');
  return ctx;
}
